import { Link } from "react-router-dom";
import { Seo, ORG_SCHEMA } from "@/components/Seo";
import { ArrowUpRight, Target, Layers, ShieldCheck, Compass, BookOpen, Waves, Wrench } from "lucide-react";

const FOUNDER_IMG = "https://static.prod-images.emergentagent.com/jobs/323e9ab2-da64-4c92-802e-e98970355c0c/images/e8c4db4a0115eb77c3be230189475ac28b17991e0bdd336d8cf79e3532af2da1.jpeg";

// Set FOUNDER_NAME to the founder's real full name to publish Person structured data.
const FOUNDER_NAME = "";
const ABOUT_JSONLD = [ORG_SCHEMA];
if (FOUNDER_NAME) {
  ABOUT_JSONLD.push({
    "@context": "https://schema.org",
    "@type": "Person",
    name: FOUNDER_NAME,
    jobTitle: "Founder",
    worksFor: { "@type": "Organization", name: "Centennial Business Systems" },
    description:
      "Founder of Centennial with 40+ years of hands-on automotive, collision, dealership, service, parts, sales and HR operations experience.",
  });
}

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About the Founder | Centennial Business Systems"
        description="Four decades on the floor, turned into dealership operating systems you can actually use — the story and mission behind Centennial and the Process → Procedure → Reference framework."
        canonicalPath="/about"
        jsonLd={ABOUT_JSONLD}
      />
      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-20 pb-12">
        <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Our story</span>
        <h1 className="font-heading font-black text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[1.03]">
          Four decades on the floor, turned into systems you can actually use.
        </h1>
        <p className="text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">
          Centennial exists to do one thing well: take hard-won operational experience and turn it into
          clear <span className="text-foreground font-medium">Process → Procedure → Reference</span> that
          real managers and employees can put to work today.
        </p>
      </section>

      {/* FOUNDER STORY */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <img src={FOUNDER_IMG} alt="Decades of hands-on operations experience turned into documented systems"
            className="w-full h-[460px] object-cover rounded-sm" data-testid="about-founder-image" />
          <div className="space-y-5 text-[17px] text-muted-foreground leading-relaxed">
            <p>
              Centennial's founder is an entrepreneur, operations leader and business systems developer with
              more than <span className="text-foreground font-semibold">40 years of experience</span> across
              the automotive, collision repair, dealership, service and franchise industries — with a focus on
              fixed operations, customer service, process improvement, workforce development and the practical
              systems that help organizations perform consistently.
            </p>
            <p>
              As the founder of Centennial Planning Group and Centennial Business Systems, Mr. Beach is
              transforming decades of hands-on experience into practical tools for today's businesses. His
              flagship initiative, the <span className="text-foreground font-medium">Centennial Dealership
              Operating System</span>, brings together operating procedures, management forms, compliance
              resources, training materials, job descriptions, performance measures and department-specific
              toolkits. Rather than offering theory alone, these resources are built for real managers and
              employees facing real operational challenges.
            </p>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-20 text-center">
          <p className="font-heading font-black text-2xl lg:text-4xl tracking-tight leading-[1.2]">
            "Success isn't measured only by what one person builds. It's measured by what they teach, what
            others can carry forward, and how many people are better equipped because that knowledge was
            passed on."
          </p>
          <p className="text-white/50 mt-6 text-sm uppercase tracking-[0.2em]">The Centennial philosophy</p>
        </div>
      </section>

      {/* BUILDER + TEACHER */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-5 text-[17px] text-muted-foreground leading-relaxed">
            <h2 className="font-heading font-black text-3xl tracking-tight text-foreground">A builder and a teacher</h2>
            <p>
              Across its growing family of ventures, Centennial operates with the mindset of both a builder and
              a teacher. The organization identifies gaps, develops practical solutions, and turns experience
              into repeatable processes that others can use.
            </p>
            <p>
              The guiding philosophy is simple: strong organizations are built through clear processes,
              accountable leadership, consistent service — and a willingness to share the lessons that work.
            </p>
          </div>
          <div className="grid gap-5">
            {[
              [Wrench, "Centennial Business Systems", "The flagship — CDOS, CCOS and CHRS: complete operating systems for dealerships, collision centers and HR, built from real-store experience."],
              [BookOpen, "My Centennial Plan", "A retirement-readiness and life-planning platform that makes complicated subjects easier — so valuable knowledge gets passed to the next generation."],
              [Waves, "Sustainable recreation, Hawaii", "Environmentally responsible, family-friendly electric boating — hospitality, conservation and low-impact technology in one scalable model."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="flex gap-4 bg-white border border-border p-6">
                <Icon className="w-6 h-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-bold text-lg">{t}</h3>
                  <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-secondary border-y border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-8">
          {[
            [Target, "Operational clarity", "Every role and process documented so nothing lives only in someone's head."],
            [ShieldCheck, "Compliance confidence", "OSHA, safety, security, and cash-handling procedures that keep you audit-ready."],
            [Layers, "Built to scale", "From a single rooftop to a full dealer group — one consistent operating standard."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="bg-white border border-border p-8">
              <Icon className="w-7 h-7 text-primary mb-5" />
              <h3 className="font-heading font-bold text-xl">{t}</h3>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ALWAYS SELLING TEASER */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
        <div className="border border-primary/30 bg-primary/5 p-8 lg:p-12 flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="flex-1">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold">
              <Compass className="w-4 h-4" /> Coming soon
            </span>
            <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tight mt-3">
              "Always Selling" — our first eBook & course
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
              The mindset that turns every phone call, greeting and hand-off into confidence, trust and the next
              opportunity — built on the same Process → Procedure → Reference approach. Releasing soon, right
              here on the site.
            </p>
          </div>
          <Link to="/resources" data-testid="about-always-selling-cta"
            className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 font-semibold hover:bg-primary hover:text-white transition-colors whitespace-nowrap">
            Read the preview <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 pb-24 text-center">
        <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Ready to systemize your store?</h2>
        <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-4 font-semibold mt-8 btn-lift" data-testid="about-products-cta">
          Explore the products <ArrowUpRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
